import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { api, PG_PHONE, PG_PHONE_TEL, PG_EMAIL } from "../lib/api";
import { CheckCircle2, Phone, Mail, MessageSquare } from "lucide-react";

export default function BookingSuccess() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setBooking(r.data)).catch(() => setError("Booking not found"));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-10 text-center" data-testid="booking-success">
          <div className="h-14 w-14 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] inline-flex items-center justify-center mx-auto">
            <CheckCircle2 size={30} />
          </div>
          <div className="eyebrow mt-5">Request received</div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0A1128] mt-3 tracking-tight">
            Your booking is in the queue!
          </h1>
          <p className="text-[#475569] mt-3 text-sm">
            Our team will review your request and reach out within minutes to confirm details and provide
            deposit instructions.
          </p>

          {error && <p className="mt-5 text-sm text-red-600">{error}</p>}

          {booking && (
            <div className="mt-8 text-left bg-[#F8FAFC] rounded-sm p-6 border border-[#E2E8F0]" data-testid="booking-summary">
              <div className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">Booking ID</div>
              <div className="font-mono text-sm text-[#0A1128] mt-1 break-all">{booking.id}</div>

              <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
                <Info label="Service" value={booking.service.replace(/_/g, " ")} />
                <Info label="Condition" value={booking.condition} />
                <Info label="When" value={`${booking.preferred_date} @ ${booking.preferred_time}`} />
                <Info label="Status" value={booking.status.replace(/_/g, " ")} />
                <Info label="Total" value={`$${Number(booking.pricing?.total || 0).toFixed(2)}`} />
                <Info label="Deposit (50%)" value={`$${Number(booking.pricing?.deposit || 0).toFixed(2)}`} />
              </div>

              <div className="mt-6 pt-5 border-t border-[#E2E8F0] text-xs text-[#64748B] leading-relaxed">
                50% deposit is required to confirm your booking. Please have it ready via cash or Zelle
                when we contact you.
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={`tel:${PG_PHONE_TEL}`} data-testid="success-call-btn" className="btn-navy inline-flex items-center gap-2 px-6 py-3 rounded-sm text-sm">
              <Phone size={16} /> Call {PG_PHONE}
            </a>
            <a href={`sms:${PG_PHONE_TEL}`} data-testid="success-text-btn" className="btn-outline-navy inline-flex items-center gap-2 px-6 py-3 rounded-sm text-sm">
              <MessageSquare size={16} /> Text us
            </a>
            <a href={`mailto:${PG_EMAIL}`} data-testid="success-email-btn" className="btn-outline-navy inline-flex items-center gap-2 px-6 py-3 rounded-sm text-sm">
              <Mail size={16} /> Email
            </a>
          </div>

          <Link to="/" data-testid="success-home-link" className="inline-block mt-8 text-sm text-[#64748B] hover:text-[#0A1128]">
            Back to home →
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.16em] text-[#94A3B8]">{label}</div>
      <div className="text-sm text-[#0A1128] mt-1 capitalize">{value}</div>
    </div>
  );
}
